import { AlertCircle, Calendar, FileText, Shield, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateFieldsQuery,
} from '@/services/api/generated/templatesV2Service';
import { CoverageCategory } from '@/services/types/coverage-types';
import type { DocumentTemplate , TemplateCategory } from '@/services/types/letters-types';
import {
  DocumentFieldMapping,
} from '@/services/types/letters-types';

import { CoverageLoRForm } from './CoverageLoRForm';
import { CoveragePicker } from './CoveragePicker';

interface CreateLoRDialogProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  template: DocumentTemplate;
  firmId: string;
}

// Helper to determine which coverage categories are relevant for a template category
const getRelevantCoverageCategories = (
  templateCategory: TemplateCategory,
): CoverageCategory[] => {
  switch (templateCategory) {
    case 'LOR_FIRST_PARTY':
      return [CoverageCategory.FIRST_PARTY];
    case 'LOR_HEALTH':
      return [CoverageCategory.FIRST_PARTY_HEALTH];
    case 'LOR_THIRD_PARTY':
      return [CoverageCategory.THIRD_PARTY];
    case 'LOR_GENERIC':
    default:
      // Generic LoR - show all categories
      return [
        CoverageCategory.FIRST_PARTY,
        CoverageCategory.FIRST_PARTY_HEALTH,
        CoverageCategory.THIRD_PARTY,
      ];
  }
};

// Field organization for better UX
export const FIELD_SECTIONS = {
  client: {
    title: 'Client Information',
    icon: User,
    description: 'Basic client details and contact information',
    fields: [
      DocumentFieldMapping.CLIENT_NAME,
      DocumentFieldMapping.CLIENT_EMAIL_ADDRESS,
      DocumentFieldMapping.CLIENT_PHONE_NUMBER,
      DocumentFieldMapping.CLIENT_DATE_OF_BIRTH,
      DocumentFieldMapping.CLIENT_LAST_NAME,
      DocumentFieldMapping.CLIENT_ADDRESS_1,
      DocumentFieldMapping.CLIENT_ADDRESS_2,
      DocumentFieldMapping.MANAGING_PARALEGAL_NAME,
      DocumentFieldMapping.MANAGING_PARALEGAL_INITIALS,
      DocumentFieldMapping.MANAGING_PARALEGAL_NUMBER,
      DocumentFieldMapping.MANAGING_PARALEGAL_EMAIL,
    ],
    color: 'blue' as const,
  },
  insurance: {
    title: 'Insurance Details',
    icon: Shield,
    description: 'Policy and coverage information',
    fields: [
      DocumentFieldMapping.CARRIER_NAME,
      DocumentFieldMapping.CARRIER_ADDRESS_1,
      DocumentFieldMapping.CARRIER_ADDRESS_2,
      DocumentFieldMapping.POLICY_NUMBER,
      DocumentFieldMapping.CLAIM_NUMBER,
      DocumentFieldMapping.INSURED_NAME,
      DocumentFieldMapping.ADJUSTER_NAME,
      DocumentFieldMapping.ADJUSTER_CONTACT,
    ],
    color: 'green' as const,
  },
  incident: {
    title: 'Incident Details',
    icon: Calendar,
    description: 'Date of loss and incident information',
    fields: [
      DocumentFieldMapping.DATE_OF_LOSS,
      DocumentFieldMapping.ACCIDENT_LOCATION,
      DocumentFieldMapping.TODAYS_DATE,
      DocumentFieldMapping.LOSS_TIME,
      DocumentFieldMapping.PLUS_2_HOURS_LOSS_TIME,
      DocumentFieldMapping.MINUS_2_HOURS_LOSS_TIME,
      DocumentFieldMapping.ONE_WEEK_FROM_TODAYS_DATE,
      DocumentFieldMapping.LOSS_FACTS,
      DocumentFieldMapping.CMS_FILE_NUMBER,
      DocumentFieldMapping.AT_FAULT_NAME,
      DocumentFieldMapping.AT_FAULT_ADDRESS_1,
      DocumentFieldMapping.AT_FAULT_ADDRESS_2,
      DocumentFieldMapping.PREMISES_NAME,
      DocumentFieldMapping.PREMISES_ADDRESS_1,
      DocumentFieldMapping.PREMISES_ADDRESS_2,
    ],
    color: 'orange' as const,
  },
};

export const CreateLoRDialog = ({
  isOpen,
  onClose,
  matterId,
  template,
  firmId,
}: CreateLoRDialogProps) => {
  const isExternal = template.format === 'EXTERNAL';

  const [selectedCoverageId, setSelectedCoverageId] = useState<
    string | undefined
  >();

  const {
    data: coverages,
    isLoading: isCoveragesLoading,
    error: coveragesError,
  } = useGetCoveragesQuery({ matterId }, { skip: isExternal });

  // Fetch template fields to determine which are required
  const {
    data: rawTemplateFields,
    isLoading: isTemplateFieldsLoading,
    error: templateFieldsError,
  } = useTemplateFieldsQuery({ id: template.id }, { skip: isExternal });
  const templateFields = rawTemplateFields
    ? { fields: (rawTemplateFields as unknown as ReadTemplateField[]) }
    : undefined;

  // Filter coverages based on template category
  const relevantCategories = getRelevantCoverageCategories(
    template.category,
  );
  const filteredCoverages =
    coverages?.filter(
      (coverage) =>
        coverage.category && relevantCategories.includes(coverage.category),
    ) || [];

  // Clear selection if the previously selected coverage is no longer available
  useEffect(() => {
    if (
      selectedCoverageId &&
      !filteredCoverages.find((c) => c.id === selectedCoverageId)
    ) {
      setSelectedCoverageId(undefined);
    }
  }, [filteredCoverages, selectedCoverageId]);

  // Create set of important fields from API response
  const importantFields = useMemo(() => {
    if (!templateFields?.fields) return new Set<DocumentFieldMapping>();

    return new Set<DocumentFieldMapping>(
      templateFields.fields
        .map((field: ReadTemplateField) => (field.context_key ?? field.reference) as DocumentFieldMapping)
        .filter(Boolean),
    );
  }, [templateFields]);

  // Guard against EXTERNAL (Adobe Sign) templates that cannot be rendered locally
  if (isExternal) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>External Template</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This template is managed through Adobe Sign and cannot be created
            here. Please use Adobe Sign to send this document.
          </p>
          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isLoading = isCoveragesLoading || isTemplateFieldsLoading;
  const hasError = !!(coveragesError || templateFieldsError);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={isLoading ? undefined : (open) => !open && onClose()}
    >
      <DialogContent className="max-w-6xl max-h-[95vh] flex flex-col">
        <DialogHeader className="flex-shrink-0 border-b border-gray-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-gray-900">
                Create Letter of Representation
              </DialogTitle>
              <p className="text-gray-600 text-sm mt-0.5">{template.name}</p>
              {template.attorney_name && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Attorney: {template.attorney_name}
                </p>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {hasError && (
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p className="font-medium">Unable to load form data</p>
                    <p className="text-sm">
                      Please check your connection and try again.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Coverage Picker Section */}
            <Card className="border-gray-200 bg-white shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  Select Coverage Policy
                </CardTitle>
                <CardDescription className="text-sm">
                  Choose the insurance policy this letter will address
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CoveragePicker
                  coverages={filteredCoverages}
                  selectedCoverageId={selectedCoverageId}
                  onCoverageChange={setSelectedCoverageId}
                  isLoading={isCoveragesLoading}
                />
              </CardContent>
            </Card>

            <div className="space-y-6 lg:grid lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:gap-6 lg:space-y-0">
              <div>
                {selectedCoverageId ? (
                  <CoverageLoRForm
                    key={selectedCoverageId}
                    matterId={matterId}
                    coverageId={selectedCoverageId}
                    template={template}
                    importantFields={importantFields}
                    fieldSections={FIELD_SECTIONS}
                    onSuccess={onClose}
                  />
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-600">
                    Select a coverage to populate the LoR merge fields.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
